// @flow
import { authPost, authGet } from '../../api';
import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import * as schema from './schema';
import {
  getLatestReferringParams,
  getFirstReferringParams,
} from '../../common/branch';

/**
 * REGISTER
 * Registers a new user through the api.
 */
export async function register(
  firstName: string,
  lastName: string,
  phone: string,
  cpf: string,
  email: string,
  password: string,
  referralCode: string,
  recaptchaToken: string,
) {
  const body = {
    first_name: firstName,
    last_name: lastName,
    phone,
    cpf,
    email,
    password,
    recaptcha_token: recaptchaToken,
    latestBranchParams: await getLatestReferringParams(), // params from last open
    firstBranchParams: await getFirstReferringParams(), // params from first open
    referral_code: referralCode,
    channel: 'spree'
  };
  const response = await authPost(`/v2/users`, body);
  const data = await response.json().then(b => camelizeKeys(b));
  const normalized = normalize(data, schema.user);
  return normalized;
}

/**
 * LOGIN
 * Logins in a user, returning object with JWT and User info
 */
export async function login(
  email: string,
  password: string,
  remember: boolean,
) {
  const orderToken = localStorage.getItem('order-guest-token');
  const body = { email, password, order_token: orderToken };
  const response = await authPost(`/v2/auth/`, body);
  if (!response.ok) throw new Error(response.status);

  // Delete the local storage order token
  const data = await response.json().then(b => camelizeKeys(b));

  if (data.message && !data.token) {
    return { mfaRequired: true, message: data.message, email, phone: data.phone || null };
  }

  const token = data.token;
  const normalized = normalize(data.user, schema.user);
  localStorage.removeItem('order-guest-token');

  updateToken(token, remember);
  return normalized;
}

/**
 * UPDATE TOKEN
 */
export async function updateToken(token, remember) {
  // We use a try/catch to detect situations where localStorage quota is 0, like safari private browsing mode
  try {
    if (!remember) throw new Error('Should not remember');
    localStorage.setItem('access-token', token);
  } catch (err) {
    // Remove items from localStorage just to be sure
    localStorage.removeItem('access-token');
    // Add items to sessionStorage, not persistent
    sessionStorage.setItem('access-token', token);
  }
}

/**
 * LOGOUT
 */
export async function logout() {
  localStorage.removeItem('access-token');
  sessionStorage.removeItem('access-token');
  return;
}

/**
 * MFA - Send Code
 */
export async function sendMfaCode(email: string, channel: string) {
  const body = { email, channel };
  const response = await authPost(`/v2/mfa/send_code`, body);
  if (!response.ok) throw new Error(response.status);
  const data = await response.json().then(b => camelizeKeys(b));
  return data;
}

/**
 * MFA - Verify Code
 */
export async function verifyMfaCode(email: string, code: string) {
  const orderToken = localStorage.getItem('order-guest-token');
  const body = { email, code, order_token: orderToken };
  const response = await authPost(`/v2/mfa/verify_code`, body);
  if (!response.ok) throw new Error(response.status);
  const data = await response.json().then(b => camelizeKeys(b));
  const token = data.token;
  const normalized = normalize(data.user, schema.user);
  updateToken(token, false);
  return normalized;
}

/**
 * Validate token
 */
export async function validateToken() {
  const response = await authGet('/v2/users/me');
  const data = await response.json().then(b => camelizeKeys(b));
  const normalized = normalize(data, schema.user);
  return normalized;
}
